import React, { useState } from 'react';
import { apiService } from '../../services/api';
import { useMutation } from '../../hooks/useApi';
import logger from '../../utils/logger';
import { Eye, FileDown, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

function RaportShitjeOnline() {
  const { roli } = useAuth();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [shitjet, setShitjet] = useState({});
  const [error, setError] = useState('');
  const [faqjaAktuale, setFaqjaAktuale] = useState({});
  const perFaqe = 10;

  const { mutate: fetchRaport, loading: loadingRaport } = useMutation(
    async ({ from, to }) => {
      return await apiService.get('/raport-shitje-online', { params: { from, to } });
    },
    {
      onSuccess: (data) => {
        const faqet = {};
        for (const key in data) {
          faqet[key] = 1;
        }
        setFaqjaAktuale(faqet);
        setShitjet(data);
        setError('');
      },
      onError: () => {
        setError('Nuk mund te merret raporti i shitjeve online.');
      },
      showNotifications: false
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    fetchRaport({ from, to });
  };

  const handleExportPdf = async () => {
    if (!from || !to) {
      setError('Zgjidhni datat per PDF.');
      return;
    }
    try {
      const response = await apiService.get('/raport-shitje-online/pdf', {
        params: { from, to },
        responseType: 'blob'
      });
      const file = new Blob([response], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    } catch (err) {
      logger.error('PDF export error:', err);
      setError('Deshtoi shkarkimi i PDF-it.');
    }
  };

  const renderTabela = (data, lokacioni) => {
    const totali = data.reduce((sum, s) => sum + (Number(s.totali) || 0), 0);
    const faqja = faqjaAktuale[lokacioni] || 1;
    const offset = (faqja - 1) * perFaqe;
    const faqetTotale = Math.ceil(data.length / perFaqe);
    const faqjaData = data.slice(offset, offset + perFaqe);

    return (
      <div key={lokacioni} className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Lokacioni: {lokacioni}</h3>

        <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
          <span>Faqe {faqja} nga {faqetTotale}</span>
          <div className="flex gap-1">
            <button onClick={() => setFaqjaAktuale(prev => ({ ...prev, [lokacioni]: 1 }))} disabled={faqja === 1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsLeft size={16} /></button>
            <button onClick={() => setFaqjaAktuale(prev => ({ ...prev, [lokacioni]: faqja - 1 }))} disabled={faqja === 1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft size={16} /></button>
            <button onClick={() => setFaqjaAktuale(prev => ({ ...prev, [lokacioni]: faqja + 1 }))} disabled={faqja === faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight size={16} /></button>
            <button onClick={() => setFaqjaAktuale(prev => ({ ...prev, [lokacioni]: faqetTotale }))} disabled={faqja === faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsRight size={16} /></button>
          </div>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <div className="card-premium overflow-hidden p-0">
            <table className="table-premium">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Produkti</th>
                  <th>Madhesia</th>
                  <th>Ngjyra</th>
                  <th>Shitesi</th>
                  <th className="text-right">Sasia</th>
                  <th className="text-right">Cmimi</th>
                  <th className="text-right">Totali</th>
                </tr>
              </thead>
              <tbody>
                {faqjaData.map((s, i) => (
                  <tr key={i}>
                    <td>{s.data}</td>
                    <td className="font-medium">{s.produkti}</td>
                    <td>{s.madhesia}</td>
                    <td>{s.ngjyrat}</td>
                    <td>{s.shitesi}</td>
                    <td className="text-right">{s.sasia}</td>
                    <td className="text-right">{Number(s.cmimi || 0).toFixed(2)} Den</td>
                    <td className="text-right font-semibold">{Number(s.totali || 0).toFixed(2)} Den</td>
                  </tr>
                ))}
                <tr className="bg-gray-100 dark:bg-dark-700 font-semibold">
                  <td colSpan="7" className="text-right">Totali:</td>
                  <td className="text-right text-green-700 dark:text-green-400">{totali.toFixed(2)} Den</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="md:hidden space-y-4">
          {faqjaData.map((s, i) => (
            <div key={i} className="card-premium p-4 space-y-1">
              <div><strong className="text-gray-700 dark:text-gray-300">Data:</strong> {s.data}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Produkti:</strong> {s.produkti}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Madhesia:</strong> {s.madhesia}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Ngjyra:</strong> {s.ngjyrat}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Shitesi:</strong> {s.shitesi}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Sasia:</strong> {s.sasia}</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Cmimi:</strong> {Number(s.cmimi || 0).toFixed(2)} Den</div>
              <div><strong className="text-gray-700 dark:text-gray-300">Totali:</strong> <span className="font-semibold">{Number(s.totali || 0).toFixed(2)} Den</span></div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gradient-logo flex items-center gap-3 animate-slide-down">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          🌐
        </div>
        Raport Shitje Online
      </h2>

      <form onSubmit={handleSubmit} className="card-premium p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="form-label">Nga data</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input-premium" required />
        </div>
        <div>
          <label className="form-label">Deri me</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input-premium" required />
        </div>
        <button type="submit" className="btn-primary mt-auto">
          <Eye size={18} /> Shfaq
        </button>
      </form>

      {error && <p className="form-error text-center">{error}</p>}

      {loadingRaport && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
        </div>
      )}

      {Object.keys(shitjet).length > 0 && (
        <div className="space-y-10">
          <button onClick={handleExportPdf} className="btn-primary">
            <FileDown size={18} /> Shkarko PDF
          </button>

          {roli === 'admin'
            ? Object.entries(shitjet).map(([lok, data]) => renderTabela(data, lok))
            : (() => {
                const emrat = Object.keys(shitjet);
                const lok = emrat.length > 0 ? emrat[0] : 'Lokacioni Im';
                const data = shitjet[lok] || [];
                return renderTabela(data, lok);
              })()}
        </div>
      )}

      {!loadingRaport && Object.keys(shitjet).length === 0 && !error && (
        <div className="empty-state">
          <p className="empty-state-title">Asnjë e dhënë për t'u shfaqur</p>
          <p className="empty-state-description">Zgjidhni datat dhe klikoni "Shfaq" për të parë raportin.</p>
        </div>
      )}
    </div>
  );
}

export default RaportShitjeOnline;
